import React, { useState, useEffect } from 'react';

import EmailBuilder from '../EmailBuilder';
import { stripEmailBorders } from '../../utils/stripEmailBorders';
import { applyPreviewFonts, previewFontCss } from '../../utils/emailFonts';
import {
  X,
  Eye
} from 'lucide-react';

const EmailConditionModal = ({
  campaignId,
  formId,
  formFields,
  condition,
  onClose,
  onSave,
  api,
  campaign // Add campaign prop to access logo
}) => {
  const initialFormData = {
    condition_name: '',
    condition_description: '',
    is_enabled: true,
    condition_config: {
      field_name: '',
      operator: 'equals',
      value: ''
    },
    from_name: '',
    from_email_address: '',
    email_subject: '',
    email_body: ''
  };
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Available variables for drag and drop
  const baseVariables = [
    { variable: '{{campaign_name}}', description: 'Campaign name' },
    { variable: '{{entry_number}}', description: 'Entry number' },
    { variable: '{{form_data}}', description: 'Full form submission details' },
    { variable: '{{campaign_logo}}', description: 'Campaign logo URL (use in img src)' },
    { variable: '{{logo_url}}', description: 'Logo URL from theme' },
    { variable: '{{primary_color}}', description: 'Primary theme color' },
    { variable: '{{secondary_color}}', description: 'Secondary theme color' },
    { variable: '{{accent_color}}', description: 'Accent theme color' }
  ];
  // Every field on the form can be inserted by its own name, e.g. {{email}}
  const availableVariables = [
    ...baseVariables,
    ...(formFields || [])
      .filter((f) => f.field_name && !baseVariables.some((v) => v.variable === `{{${f.field_name}}}`))
      .map((f) => ({ variable: `{{${f.field_name}}}`, description: `Form field: ${f.field_label || f.field_name}` }))
  ];

  // Available operators for different field types
  const operators = {
    text: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does Not Contain' }
    ],
    email: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' }
    ],
    phone: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' }
    ],
    dropdown: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' }
    ],
    radio: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' }
    ],
    checkbox: [
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does Not Contain' }
    ],
    textarea: [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does Not Contain' }
    ]
  };

  useEffect(() => {
    if (condition) {
      setFormData({
        condition_name: condition.condition_name || '',
        condition_description: condition.condition_description || '',
        is_enabled: condition.is_enabled !== false,
        condition_config: condition.condition_config || {
          field_name: '',
          operator: 'equals',
          value: ''
        },
        from_name: condition.from_name || '',
        from_email_address: condition.from_email_address || '',
        email_subject: condition.email_subject || '',
        email_body: condition.email_body || ''
      });
    } else {
      setFormData({
        ...initialFormData,
        condition_config: { ...initialFormData.condition_config }
      });
    }
  }, [condition]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };


  const handleConditionConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      condition_config: {
        ...prev.condition_config,
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[`condition_config.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`condition_config.${field}`]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.condition_name.trim()) {
      newErrors.condition_name = 'Condition name is required';
    }

    if (!formData.condition_config.field_name) {
      newErrors['condition_config.field_name'] = 'Field is required';
    }

    if (!formData.condition_config.value.trim()) {
      newErrors['condition_config.value'] = 'Value is required';
    }

    if (!formData.email_subject.trim()) {
      newErrors.email_subject = 'Email subject is required';
    }

    if (formData.from_email_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.from_email_address.trim())) {
      newErrors.from_email_address = 'Enter a valid email address or leave blank';
    }

    if (!formData.email_body.trim()) {
      newErrors.email_body = 'Email body is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        form_id: formId
      };

      if (condition) {
        await api.put(`/admin/campaigns/${campaignId}/email-conditions/${condition.id}`, payload);
      } else {
        await api.post(`/admin/campaigns/${campaignId}/email-conditions`, payload);
      }

      onSave();
    } catch (err) {
      console.error('Failed to save email condition:', err);
      alert('Failed to save email condition: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getSelectedFieldType = () => {
    const selectedField = formFields.find(field => field.field_name === formData.condition_config.field_name);
    return selectedField?.field_type || 'text';
  };

  const getFieldOptions = () => {
    const selectedField = formFields.find(field => field.field_name === formData.condition_config.field_name);
    if (selectedField?.field_options?.options) {
      return selectedField.field_options.options;
    }
    return [];
  };

  const handlePreview = () => {
    // Always use the actual email_body from settings and ensure it's a string
    let emailBody = formData.email_body || '';
    
    // Ensure emailBody is a string
    if (typeof emailBody !== 'string') {
      emailBody = String(emailBody);
    }
    
    // Replace variables with sample data for preview
    const sampleData = {
      campaign_name: campaign?.name || 'Sample Campaign',
      first_name: 'John',
      user_name: 'John Doe',
      user_email: 'john@example.com',
      entry_number: '12345',
      submission_date: new Date().toLocaleString(),
      form_data: 'Sample form submission data...',
      primary_color: campaign?.primary_color || '#1a5490',
      secondary_color: campaign?.secondary_color || '#4CAF50',
      accent_color: campaign?.accent_color || '#FF5722'
    };

    // Replace all variables except campaign_logo
    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      emailBody = emailBody.replace(regex, sampleData[key]);
    });

    // Special handling for campaign_logo to prevent double encoding
    if (campaign?.logo_url) {
      emailBody = emailBody.replace(/\{\{campaign_logo\}\}/g, campaign.logo_url);
    } else {
      emailBody = emailBody.replace(/\{\{campaign_logo\}\}/g, 'https://via.placeholder.com/200x80/14B8A6/ffffff?text=Campaign+Logo');
    }

    // Add CSS styles for proper email rendering
    const emailWithStyles = `
      <style>
        ul { list-style-type: disc; margin-left: 20px; padding-left: 0; }
        li { margin-bottom: 8px; }
        ol { list-style-type: decimal; margin-left: 20px; padding-left: 0; }
        p { margin: 10px 0; }
        h1, h2, h3, h4, h5, h6 { margin: 15px 0 10px 0; }
        img { max-width: 100%; height: auto; }
        table { border-collapse: collapse; width: 100%; }
        td, th { padding: 8px; }
        a { color: #007bff; text-decoration: underline; }
        .email-preview-root { font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #333; }
        .email-preview-root * { font-family: inherit; }
        ${previewFontCss(campaign?.font_settings)}
        .email-preview-root b, .email-preview-root strong, .email-preview-root h1, .email-preview-root h2, .email-preview-root h3 { font-weight: 700; }
      </style>
      <div class="email-preview-root">${applyPreviewFonts(stripEmailBorders(emailBody), campaign?.font_settings)}</div>
    `;

    return emailWithStyles;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {condition ? 'Edit Email Notification Condition' : 'Create Email Notification Condition'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.condition_name}
                  onChange={(e) => handleInputChange('condition_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.condition_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Entry Method 1 Notification"
                />
                {errors.condition_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.condition_name}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_enabled"
                  checked={formData.is_enabled}
                  onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <label htmlFor="is_enabled" className="text-sm font-medium text-gray-700">
                  Enable this condition
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (optional)
              </label>
              <textarea
                value={formData.condition_description}
                onChange={(e) => handleInputChange('condition_description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Optional description of this condition"
              />
            </div>

            {/* Condition Configuration */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Condition Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.condition_config.field_name}
                    onChange={(e) => {
                      handleConditionConfigChange('field_name', e.target.value);
                      // Reset operator and value when field changes
                      handleConditionConfigChange('operator', 'equals');
                      handleConditionConfigChange('value', '');
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors['condition_config.field_name'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a field</option>
                    {formFields.map((field) => (
                      <option key={field.id} value={field.field_name}>
                        {field.field_label} ({field.field_type})
                      </option>
                    ))}
                  </select>
                  {errors['condition_config.field_name'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['condition_config.field_name']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Operator <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.condition_config.operator}
                    onChange={(e) => handleConditionConfigChange('operator', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {operators[getSelectedFieldType()]?.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Value <span className="text-red-500">*</span>
                  </label>
                  {['dropdown', 'radio'].includes(getSelectedFieldType()) ? (
                    <select
                      value={formData.condition_config.value}
                      onChange={(e) => handleConditionConfigChange('value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors['condition_config.value'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a value</option>
                      {getFieldOptions().map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.condition_config.value}
                      onChange={(e) => handleConditionConfigChange('value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors['condition_config.value'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter value to match"
                    />
                  )}
                  {errors['condition_config.value'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['condition_config.value']}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Configuration */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Email Configuration</h4>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Name
                    </label>
                    <input
                      type="text"
                      value={formData.from_name}
                      onChange={(e) => handleInputChange('from_name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent border-gray-300"
                      placeholder="Defaults to campaign name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Email
                    </label>
                    <input
                      type="email"
                      value={formData.from_email_address}
                      onChange={(e) => handleInputChange('from_email_address', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.from_email_address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Defaults to global sender"
                    />
                    {errors.from_email_address && (
                      <p className="text-red-500 text-sm mt-1">{errors.from_email_address}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.email_subject}
                    onChange={(e) => handleInputChange('email_subject', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.email_subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., New Entry Method 1 Submission"
                  />
                  {errors.email_subject && (
                    <p className="text-red-500 text-sm mt-1">{errors.email_subject}</p>
                  )}
                </div>


                <div>
                  {!showPreview && (
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Body <span className="text-red-500">*</span>
                    </label>
                  )}
                  
                  {!showPreview ? (
                    <>
                      <EmailBuilder
                        value={typeof formData.email_body === 'string' ? formData.email_body : ''}
                        onChange={(html) => handleInputChange('email_body', html)}
                        variables={availableVariables}
                      />
                    </>
                  ) : (
                    /* Email Preview */
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700">Email Preview</h4>
                      </div>
                      <div className="p-4 bg-white max-h-96 overflow-auto">
                        <div dangerouslySetInnerHTML={{ __html: handlePreview() }} />
                      </div>
                    </div>
                  )}
                  
                  {errors.email_body && (
                    <p className="text-red-500 text-sm mt-1">{errors.email_body}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Hide Preview' : 'Preview Email'}
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (condition ? 'Update Condition' : 'Create Condition')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConditionModal;
